import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Link,
  Collapse,
  Divider,
  Switch,
  FormControlLabel,
  FormGroup,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Cookie,
  ExpandMore,
  ExpandLess,
  Security,
  Campaign,
  Build,
} from '@mui/icons-material';
import { useCookieConsent } from '../contexts/CookieContext';

const CookieBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showBanner, acceptAll, declineAll, savePreferences, preferences } = useCookieConsent();
  const [showPreferences, setShowPreferences] = useState(false);
  const [tempPreferences, setTempPreferences] = useState(preferences);
  const [hasScrolled, setHasScrolled] = useState(false);

  // Handle scroll to accept cookies
  useEffect(() => {
    if (!showBanner) return;

    const handleScroll = () => {
      if (window.scrollY > 100 && !hasScrolled) {
        setHasScrolled(true);
        acceptAll();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showBanner, hasScrolled, acceptAll]);

  if (!showBanner) return null;

  const handlePreferenceChange = (cookieType) => (event) => {
    setTempPreferences({
      ...tempPreferences,
      [cookieType]: event.target.checked,
    });
  };

  const handleSavePreferences = () => {
    savePreferences(tempPreferences);
  };

  const cookieTypes = [
    {
      key: 'necessary',
      title: 'Necessary',
      description: 'Required for the website to function properly.',
      icon: <Security />,
      required: true,
    },
    {
      key: 'functional',
      title: 'Functional',
      description: 'Enable enhanced functionality like preferences.',
      icon: <Build />,
      required: false,
    },
    {
      key: 'marketing',
      title: 'Marketing',
      description: 'Used to track visitors for advertising purposes.',
      icon: <Campaign />,
      required: false,
    },
  ];

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          m: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: 2,
          overflow: 'hidden',
          pointerEvents: 'auto',
          maxWidth: '600px',
          mx: 'auto',
        }}
      >
        {/* Compact Banner */}
        <Box sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Cookie sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                We use cookies to improve your experience.{' '}
                <Link href="/privacy" target="_blank" sx={{ color: 'primary.main' }}>
                  Learn more
                </Link>
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setShowPreferences(!showPreferences)}
                endIcon={showPreferences ? <ExpandLess /> : <ExpandMore />}
                sx={{ 
                  minWidth: 'auto',
                  color: 'text.secondary',
                  fontSize: '0.8rem'
                }}
              >
                Options
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={declineAll}
                sx={{ 
                  borderColor: 'error.main',
                  color: 'error.main',
                  fontSize: '0.8rem',
                  minWidth: 'auto',
                  px: 1.5
                }}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={acceptAll}
                sx={{ 
                  backgroundColor: 'primary.main',
                  fontSize: '0.8rem',
                  minWidth: 'auto',
                  px: 1.5
                }}
              >
                Accept
              </Button>
            </Stack>
          </Stack>

          {/* Scroll hint */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: 'text.secondary',
              mt: 1,
              fontSize: '0.7rem',
            }}
          >
            Scroll down to accept all cookies
          </Typography>
        </Box>

        {/* Preferences Panel */}
        <Collapse in={showPreferences}>
          <Divider />
          <Box sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Cookie Preferences
            </Typography>

            <FormGroup>
              {cookieTypes.map((cookieType) => (
                <Box key={cookieType.key} sx={{ mb: 1.5 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {React.cloneElement(cookieType.icon, { sx: { fontSize: 16 } })}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {cookieType.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cookieType.description}
                        </Typography>
                      </Box>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={tempPreferences[cookieType.key]}
                          onChange={handlePreferenceChange(cookieType.key)}
                          disabled={cookieType.required}
                          size="small"
                        />
                      }
                      label=""
                      sx={{ ml: 1 }}
                    />
                  </Stack>
                </Box>
              ))}
            </FormGroup>

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowPreferences(false)}
                sx={{ fontSize: '0.8rem' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSavePreferences}
                sx={{ fontSize: '0.8rem' }}
              >
                Save
              </Button>
            </Stack>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default CookieBanner;